import { useEffect } from 'react';
import { buildMeta, SITE_NAME } from '../shared/seo.js';
import type { Article, Category } from '../lib/repo';

export type Meta = ReturnType<typeof buildMeta>;

export function siteUrl(): string {
  const fromEnv = import.meta.env?.VITE_SITE_URL as string | undefined;
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') return window.location.origin;
  return 'https://thestoryline.xyz';
}

/** Apply a meta object to the live document (used during SPA navigation). */
export function applyMeta(meta: Meta): void {
  document.title = meta.title;
  const upsert = (selector: string, create: () => HTMLElement, set: (el: HTMLElement) => void) => {
    let el = document.head.querySelector<HTMLElement>(selector);
    if (!el) { el = create(); document.head.appendChild(el); }
    set(el);
  };
  const setMeta = (key: 'name' | 'property', name: string, content: string) =>
    upsert(`meta[${key}="${name}"]`, () => { const m = document.createElement('meta'); m.setAttribute(key, name); return m; }, (el) => el.setAttribute('content', content));

  setMeta('name', 'description', meta.description);
  setMeta('name', 'robots', meta.robots);
  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:type', meta.ogType);
  setMeta('property', 'og:title', meta.title);
  setMeta('property', 'og:description', meta.description);
  setMeta('property', 'og:url', meta.canonical);
  setMeta('property', 'og:image', meta.ogImage);
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', meta.title);
  setMeta('name', 'twitter:description', meta.description);
  setMeta('name', 'twitter:image', meta.ogImage);
  if (meta.publishedTime) setMeta('property', 'article:published_time', meta.publishedTime);
  if (meta.modifiedTime) setMeta('property', 'article:modified_time', meta.modifiedTime);
  if (meta.author) setMeta('property', 'article:author', meta.author);

  upsert('link[rel="canonical"]', () => { const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l; }, (el) => el.setAttribute('href', meta.canonical));

  const ldId = 'tsl-jsonld';
  document.getElementById(ldId)?.remove();
  if (meta.jsonLd) {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.id = ldId;
    s.textContent = JSON.stringify(meta.jsonLd);
    document.head.appendChild(s);
  }
}

/**
 * Keeps the document head correct for the current route. The server already
 * injects the same tags for crawlers; this only matters for in-app navigation.
 */
export function Seo({ name, article, category }: { name: string; article?: Article | null; category?: Category | null }) {
  useEffect(() => {
    const meta = buildMeta({
      route: { name, params: {} },
      article: article ?? undefined,
      category: category ?? undefined,
      siteUrl: siteUrl(),
    });
    applyMeta(meta);
  }, [name, article, category]);
  return null;
}
